import { ShieldCheck } from '@music-vine/cadence/icons';
import { SafelistChannelPromptContent } from './SafelistChannelPrompt/SafelistChannelPromptContent';
import { SAFELIST_PROMPT_STORAGE_KEY } from './SafelistChannelPrompt/useSafelistChannelPromptEligibility';
import type { PageDialogProps } from '@/components/PageDialog/PageDialog';
const openMobileDrawer = () => {
  window.postMessage({
    type: 'OPEN_DIALOGUE_DRAWER'
  }, window.location.origin);
};

// Music pages only (per DEV-5907): music browse/track/collection pages and
// trending. Everything else (SFX, stock video, music generator, LUTs,
// pricing, blog, help center, refer & earn, account area, download history)
// is out of scope. Mirrors the adaptive-music dialogue's allow-list.
const showOnSlugs = [/^\/music(?:\/|$)/, /^\/trending(?:\/|$)/];

// Carve the customize workspace itself out of the /music allow-list.
const customizeRegex = /^\/music\/customize(?:\/|$)/;

/**
 * Prompt nudging paying subscribers to safelist their YouTube channel
 * (DEV-5907). Surfaced through `PageDialogQueue` on music pages — eligibility
 * is decided by `useSafelistChannelPromptEligibility`. Dismissal (Esc / X /
 * drag-down) is stored in sessionStorage so the prompt returns next session,
 * until the user opts out permanently via "Don't show this message again".
 * The subscription success paths write the same sessionStorage key (see
 * `blockSafelistPromptForSession`), so a brand-new subscriber first sees the
 * prompt in their next session.
 */
export const getSafelistChannelPromptProps = ({
  isEligible
}: {
  isEligible: boolean;
}): PageDialogProps | null => {
  if (!isEligible) {
    return null;
  }
  return {
    storageKey: SAFELIST_PROMPT_STORAGE_KEY,
    storageType: 'sessionStorage',
    title: 'Safelist your YouTube channel',
    includeSlugs: showOnSlugs,
    excludeSlugs: [customizeRegex],
    delay: 2000,
    // overflow-visible lets the channel-search results dropdown float over
    // the dialogue's bottom edge, per the Figma design. Because nothing is
    // clipped, the dark background lives on the panel itself (not the
    // content) so it stays inside the rounded corners.
    panelClassName: 'max-w-[600px] overflow-visible dark:bg-gray-875',
    drawerContentClassName: 'max-h-[90vh]',
    children: <SafelistChannelPromptContent />,
    mobileChildren: <button className="flex w-full items-center gap-3 p-3 text-left dark:bg-gray-875" onClick={openMobileDrawer} type="button">
        <span className="flex size-10 shrink-0 items-center justify-center rounded-lg bg-green-50 dark:bg-green-950">
          <ShieldCheck className="size-5 text-green-600 dark:text-green-400" />
        </span>
        <span className="text-base text-gray-950 dark:text-white">
          Safelist your YouTube channel
        </span>
      </button>,
    drawerChildren: <SafelistChannelPromptContent inDrawer />
  };
};